
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";

import {
    ArrowRight,
    BedDouble,
    BookOpen,
    CalendarDays,
    CheckCircle2,
    CircleAlert,
    Download,
    Home,
    Hotel,
    LoaderCircle,
    LockKeyhole,
    Mail,
    ReceiptText,
    RefreshCw,
    ShieldCheck,
    UserRound,
    Users,
    X,
} from "lucide-react";

const API_URL = "https://dubaibudgetivehotels.com/api";
const SERVICE_CHARGE = 0.1;
const VAT = 0.05;

const money = (value) =>
    `AED ${Number(value || 0).toLocaleString("en-AE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

const formatDate = (date) => {
    if (!date) return "—";

    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return "—";

    return new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Dubai",
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(parsed);
};

const getNights = (booking) => {
    if (!booking) return 1;

    const recorded = Number(booking.nights);
    if (Number.isFinite(recorded) && recorded > 0) return recorded;

    const start = new Date(booking.check_in);
    const end = new Date(booking.check_out);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return 1;
    }

    return Math.max(1, Math.ceil((end - start) / 86400000));
};

const calculateCharges = (booking) => {
    const base = Number(
        booking?.final_nightly_price || booking?.room_price || 0
    );

    const serviceCharge = base * SERVICE_CHARGE;
    const vat = base * VAT;
    const tourismFee = Number(booking?.hotel?.rating) === 5 ? 20 : 10;

    return {
        base: Math.round(base),
        serviceCharge: Math.round(serviceCharge),
        vat: Math.round(vat),
        tourismFee: Math.round(tourismFee),
        total: Math.round(base + serviceCharge + vat + tourismFee),
    };
};

const getWebsiteName = () => {
    const hostname = window.location.hostname
        .replace(/^www\./, "")
        .replace(/\.(com|net|org|ae|co\.uk|co\.in)$/i, "");

    if (hostname === "localhost" || hostname === "127.0.0.1") {
        return "Dubai Budgetive Hotels";
    }

    return (
        hostname
            .replace(/[-_]/g, " ")
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .replace(/\b\w/g, (char) => char.toUpperCase()) ||
        "Dubai Budgetive Hotels"
    );
};

const successStatuses = [
    "success",
    "successful",
    "paid",
    "completed",
    "confirmed",
    "captured",
];

const failureStatuses = [
    "failed",
    "failure",
    "declined",
    "cancelled",
    "canceled",
    "error",
];

export default function PaymentSuccess({ onLoginClick }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const orderId = searchParams.get("order_id");

    const [booking, setBooking] = useState(null);
    const [verification, setVerification] = useState("loading");
    const [errorMessage, setErrorMessage] = useState("");
    const [showLoginPopup, setShowLoginPopup] = useState(false);

    const websiteName = getWebsiteName();

    useEffect(() => {
        if (!orderId) {
            setVerification("error");
            setErrorMessage("Order reference is missing.");
            return;
        }

        let active = true;
        let timeoutId;
        let attempts = 0;

        const verifyPayment = async () => {
            try {
                const response = await axios.get(
                    `${API_URL}/verify-payment/${encodeURIComponent(orderId)}`
                );

                if (!active) return;

                const data = response.data || {};
                const status = String(data.status || "").toLowerCase();

                if (successStatuses.includes(status)) {
                    if (data.booking) {
                        setBooking(data.booking);
                        setVerification("success");
                        setErrorMessage("");
                    } else {
                        setVerification("error");
                        setErrorMessage(
                            "Payment was verified, but booking details are unavailable."
                        );
                    }
                    return;
                }

                if (failureStatuses.includes(status)) {
                    setVerification("failed");
                    setErrorMessage(
                        "Your payment was not completed. Contact support if you believe you were charged."
                    );
                    return;
                }

                if (status === "pending" || status === "processing") {
                    attempts += 1;
                    setVerification("pending");

                    if (attempts < 10) {
                        timeoutId = window.setTimeout(verifyPayment, 3000);
                    } else {
                        setErrorMessage(
                            "Payment confirmation is taking longer than expected. Please check again shortly."
                        );
                    }
                    return;
                }

                setVerification("error");
                setErrorMessage(
                    "We could not confirm your payment status. Please contact support."
                );
            } catch (error) {
                if (!active) return;
                console.error("Payment verification failed:", error);
                setVerification("error");
                setErrorMessage("Unable to verify payment. Please try again.");
            }
        };

        setVerification("loading");
        setBooking(null);
        setErrorMessage("");
        verifyPayment();

        return () => {
            active = false;
            window.clearTimeout(timeoutId);
        };
    }, [orderId]);

    const nights = getNights(booking);
    const charges = calculateCharges(booking);
    const totalPaid = charges.total * nights;
    const orderReference = booking?.order_id || orderId || "—";

    const handleMyBookingsClick = () => {
        let user = null;

        try {
            user = JSON.parse(localStorage.getItem("user") || "null");
        } catch {
            user = null;
        }

        if (!user) {
            setShowLoginPopup(true);
            return;
        }

        navigate("/profile");
    };

    const handleLogin = () => {
        setShowLoginPopup(false);
        onLoginClick?.();
    };

    // ============================================
    // PDF RECEIPT - NO BOTTOM FOOTER
    // ============================================
    const downloadInvoice = () => {
        if (!booking || verification !== "success") return;

        const doc = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4",
        });

        const W = 210;
        const margin = 18;
        const contentW = W - margin * 2;

        const green = [22, 139, 67];
        const darkGreen = [6, 78, 59];
        const navy = [23, 50, 77];
        const muted = [105, 121, 137];
        const border = [222, 234, 225];
        const mint = [245, 250, 247];
        const white = [255, 255, 255];

        const safe = (value) => String(value ?? "—");

        const write = (
            value,
            x,
            y,
            size = 10,
            color = navy,
            weight = "normal",
            options = {}
        ) => {
            doc.setFont("helvetica", weight);
            doc.setFontSize(size);
            doc.setTextColor(...color);
            doc.text(safe(value), x, y, options);
        };

        const line = (y) => {
            doc.setDrawColor(...border);
            doc.setLineWidth(0.3);
            doc.line(margin, y, W - margin, y);
        };

        const infoLine = (label, value, x, y, width = 77) => {
            write(label.toUpperCase(), x, y, 7.5, muted, "bold");

            const lines = doc
                .splitTextToSize(safe(value), width)
                .slice(0, 2);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(...navy);
            doc.text(lines, x, y + 6);

            return y + 6 + Math.max(lines.length, 1) * 4.5;
        };

        // PDF HEADER
        doc.setFillColor(...darkGreen);
        doc.rect(0, 0, W, 49, "F");

        doc.setFillColor(...green);
        doc.rect(0, 49, W, 2, "F");

        write(
            websiteName.toUpperCase(),
            margin,
            18,
            16,
            white,
            "bold"
        );

        write(
            "Hotel booking confirmation",
            margin,
            27,
            9,
            [185, 236, 202]
        );

        write(
            "PAYMENT RECEIPT",
            W - margin,
            17,
            11,
            white,
            "bold",
            { align: "right" }
        );

        write(
            `Issued: ${formatDate(new Date())}`,
            W - margin,
            27,
            8.5,
            [185, 236, 202],
            "normal",
            { align: "right" }
        );

        // PAYMENT STATUS
        doc.setFillColor(235, 249, 240);
        doc.roundedRect(margin, 60, contentW, 23, 3, 3, "F");

        write(
            "PAYMENT SUCCESSFUL",
            margin + 7,
            69,
            11,
            green,
            "bold"
        );

        write(
            "Your hotel booking is confirmed.",
            margin + 7,
            76,
            8.5,
            darkGreen
        );

        write("ORDER REFERENCE", margin, 95, 8, muted, "bold");
        write(orderReference, margin, 102, 12, navy, "bold");

        write(
            "RECEIPT DATE",
            W - margin,
            95,
            8,
            muted,
            "bold",
            { align: "right" }
        );

        write(
            formatDate(new Date()),
            W - margin,
            102,
            10,
            navy,
            "bold",
            { align: "right" }
        );

        line(110);

        // GUEST DETAILS
        write("GUEST DETAILS", margin, 121, 11, green, "bold");
        write("RESERVATION DETAILS", 110, 121, 11, green, "bold");

        let guestY = 131;

        guestY = infoLine("Guest name", booking.name, margin, guestY);
        guestY = infoLine("Email", booking.email, margin, guestY + 6, 79);
        guestY = infoLine("Guests", booking.guests, margin, guestY + 6);

        let hotelY = 131;

        hotelY = infoLine("Hotel", booking.hotel_name, 110, hotelY);
        hotelY = infoLine("Room type", booking.room_name, 110, hotelY + 6);

        hotelY = infoLine(
            "Room number",
            booking.room_number || booking.room?.room_number || "—",
            110,
            hotelY + 6
        );

        const detailsBottom = Math.max(guestY, hotelY) + 7;
        line(detailsBottom);

        const datesY = detailsBottom + 12;

        infoLine("Check-in", formatDate(booking.check_in), margin, datesY, 46);
        infoLine("Check-out", formatDate(booking.check_out), 81, datesY, 46);
        infoLine("Nights", nights, 151, datesY, 35);

        // PAYMENT BREAKDOWN
        let y = datesY + 25;

        doc.setFillColor(...green);
        doc.roundedRect(margin, y, contentW, 12, 2, 2, "F");

        write(
            "PAYMENT BREAKDOWN",
            margin + 5,
            y + 8,
            10,
            white,
            "bold"
        );

        write(
            "AMOUNT",
            W - margin - 5,
            y + 8,
            9,
            white,
            "bold",
            { align: "right" }
        );

        y += 20;

        const item = (label, amount, emphasize = false) => {
            if (emphasize) {
                doc.setFillColor(...mint);
                doc.rect(margin, y - 6, contentW, 12, "F");
            }

            write(
                label,
                margin + 5,
                y,
                9,
                navy,
                emphasize ? "bold" : "normal"
            );

            write(
                money(amount),
                W - margin - 5,
                y,
                9,
                navy,
                emphasize ? "bold" : "normal",
                { align: "right" }
            );

            y += 11;
        };

        item("Room price (per night)", charges.base);

        item(
            `Room price total (${nights} night${nights === 1 ? "" : "s"})`,
            charges.base * nights
        );

        item("Service charge (10%) per night", charges.serviceCharge);
        item("VAT (5%) per night", charges.vat);
        item("Tourism fee per night", charges.tourismFee);
        item("Total per night", charges.total, true);

        if (booking.discount_price) {
            item(
                "Discounted rate (recorded)",
                Number(booking.discount_price)
            );
        }

        line(y + 1);
        y += 10;

        // TOTAL PAID
        doc.setFillColor(...darkGreen);
        doc.roundedRect(margin, y - 5, contentW, 19, 3, 3, "F");

        write(
            "TOTAL PAID",
            margin + 6,
            y + 6,
            12,
            white,
            "bold"
        );

        write(
            money(totalPaid),
            W - margin - 6,
            y + 6,
            13,
            [165, 232, 184],
            "bold",
            { align: "right" }
        );

        y += 25;

        // CONTACT DETAILS
        if (y > 245) {
            doc.addPage();
            y = 25;
        }

        write(
            `Thank you for booking with ${websiteName}.`,
            margin,
            y,
            10,
            green,
            "bold"
        );

        y += 8;

        write(
            "Email: info@southtravels.com",
            margin,
            y,
            8.5,
            muted
        );

        y += 6;

        write(
            "Phone: +971 4 447 3839",
            margin,
            y,
            8.5,
            muted
        );

        y += 6;

        write(
            "WhatsApp: +971 50 247 7593",
            margin,
            y,
            8.5,
            muted
        );

        y += 8;

        write(
            "Please retain this receipt for your records.",
            margin,
            y,
            8.5,
            muted
        );

        // NO PAGE FOOTER
        // No website address
        // No "Hotel booking receipt" text
        // No horizontal footer divider

        const fileReference = String(orderReference).replace(
            /[^a-zA-Z0-9_-]/g,
            "-"
        );

        doc.save(`hotel-booking-receipt-${fileReference}.pdf`);
    };

    const guestDetails = booking
        ? [
            {
                label: "Guest Name",
                value: booking.name,
                icon: UserRound,
            },
            {
                label: "Email Address",
                value: booking.email,
                icon: Mail,
            },
            {
                label: "Guests",
                value: booking.guests,
                icon: Users,
            },
            {
                label: "Room Type",
                value: booking.room_name,
                icon: BedDouble,
            },
            {
                label: "Room Number",
                value:
                    booking.room_number ||
                    booking.room?.room_number ||
                    "—",
                icon: BedDouble,
            },
        ]
        : [];

    return (
        <main className="min-h-screen overflow-hidden bg-[#F5FAF7] pt-20 text-[#17324D]">
            {/* HERO */}
            <section className="relative isolate overflow-hidden bg-[#064E3B] px-4 pb-20 pt-14 text-center text-white sm:pb-24 sm:pt-16">
                <div className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full border border-white/10" />
                <div className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-[#168B43]/30 blur-3xl" />

                <div className="relative mx-auto max-w-[780px]">
                    <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-white/20 bg-white/10 text-[#A5E8B8]">
                        {verification === "success" ? (
                            <CheckCircle2 size={42} />
                        ) : verification === "loading" ||
                            verification === "pending" ? (
                            <LoaderCircle size={40} className="animate-spin" />
                        ) : (
                            <CircleAlert size={40} />
                        )}
                    </div>

                    <p className="mt-7 text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#B6F2CB]">
                        Hotel Reservation
                    </p>

                    <h1 className="mt-3 text-[38px] font-extrabold leading-tight tracking-tight text-white sm:text-[54px]">
                        {verification === "success" ? (
                            <>
                                Payment{" "}
                                <span className="text-[#A5E8B8]">
                                    Successful!
                                </span>
                            </>
                        ) : verification === "loading" ||
                            verification === "pending" ? (
                            <>
                                Verifying Your{" "}
                                <span className="text-[#A5E8B8]">
                                    Payment
                                </span>
                            </>
                        ) : (
                            <>
                                Payment{" "}
                                <span className="text-[#A5E8B8]">
                                    Status
                                </span>
                            </>
                        )}
                    </h1>

                    <p className="mx-auto mt-5 max-w-[600px] text-[14px] leading-8 text-white/75">
                        {verification === "success"
                            ? `Thank you for booking with ${websiteName}. Your payment has been verified and your reservation details are ready.`
                            : verification === "loading" ||
                                verification === "pending"
                                ? "We are checking your payment status and retrieving your booking details."
                                : "We could not confirm a successful payment. Please review the information below."}
                    </p>

                    {orderId && (
                        <div className="mt-7 inline-flex max-w-full items-center gap-2 rounded-full border border-white/20 bg-white/10 px-5 py-3 text-xs text-white/85">
                            <ReceiptText size={16} className="shrink-0 text-[#A5E8B8]" />
                            Reference:
                            <span className="break-all font-extrabold text-white">
                                {orderId}
                            </span>
                        </div>
                    )}
                </div>
            </section>

            {/* MAIN CONTENT */}
            <div className="relative mx-auto -mt-9 max-w-[1536px] px-4 pb-20 sm:px-6 lg:px-8">
                {verification !== "success" || !booking ? (
                    <div className="mx-auto max-w-[800px] rounded-[24px] border border-[#DCEAE1] bg-white px-6 py-14 text-center shadow-[0_15px_50px_rgba(6,78,59,0.06)] sm:px-10">
                        {verification === "loading" ||
                            verification === "pending" ? (
                            <>
                                <LoaderCircle
                                    size={42}
                                    className="mx-auto animate-spin text-[#168B43]"
                                />

                                <h2 className="mt-6 text-[26px] font-extrabold text-[#17324D]">
                                    Preparing Your Confirmation
                                </h2>

                                <p className="mt-3 text-sm leading-7 text-[#718096]">
                                    Please wait while we verify your payment with
                                    the booking system.
                                </p>

                                {errorMessage && (
                                    <p className="mt-5 text-sm text-amber-700">
                                        {errorMessage}
                                    </p>
                                )}
                            </>
                        ) : (
                            <>
                                <CircleAlert
                                    size={42}
                                    className="mx-auto text-amber-600"
                                />

                                <h2 className="mt-6 text-[26px] font-extrabold text-[#17324D]">
                                    Payment Verification Unavailable
                                </h2>

                                <p className="mx-auto mt-3 max-w-[540px] text-sm leading-7 text-[#718096]">
                                    {errorMessage}
                                </p>

                                <div className="mt-7 flex flex-wrap justify-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => window.location.reload()}
                                        className="inline-flex items-center gap-2 rounded-xl bg-[#168B43] px-6 py-3.5 text-sm font-extrabold text-white hover:bg-[#087D37]"
                                    >
                                        <RefreshCw size={16} />
                                        Check Again
                                    </button>

                                    <a
                                        href="mailto:info@southtravels.com"
                                        className="inline-flex items-center gap-2 rounded-xl border border-[#B9DEC9] px-6 py-3.5 text-sm font-extrabold text-[#168B43] hover:bg-[#F0FAF3]"
                                    >
                                        <Mail size={16} />
                                        Contact Support
                                    </a>
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="grid items-start gap-7 lg:grid-cols-[minmax(0,1fr)_380px]">
                        {/* LEFT COLUMN */}
                        <div className="space-y-7">
                            {/* RESERVATION */}
                            <section className="overflow-hidden rounded-[24px] border border-[#DCEAE1] bg-white shadow-[0_12px_40px_rgba(6,78,59,0.04)]">
                                <div className="border-b border-[#E8F0EA] p-6 sm:p-9">
                                    <div className="flex flex-wrap items-start justify-between gap-4">
                                        <div>
                                            <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#168B43]">
                                                <Hotel size={16} />
                                                Your Reservation
                                            </p>

                                            <h2 className="mt-3 text-[27px] font-extrabold leading-tight tracking-tight text-[#17324D] sm:text-[35px]">
                                                {booking.hotel_name || "Your Hotel Reservation"}
                                            </h2>

                                            <p className="mt-2 text-[13px] text-[#718096]">
                                                Your upcoming stay at a glance.
                                            </p>
                                        </div>

                                        <span className="inline-flex items-center gap-2 rounded-full bg-[#E8F7EE] px-4 py-2 text-[12px] font-extrabold text-[#168B43]">
                                            <CheckCircle2 size={16} />
                                            Confirmed
                                        </span>
                                    </div>
                                </div>

                                <div className="grid gap-4 p-6 sm:grid-cols-3 sm:p-9">
                                    {[
                                        {
                                            label: "Check-In",
                                            value: formatDate(booking.check_in),
                                            icon: CalendarDays,
                                        },
                                        {
                                            label: "Check-Out",
                                            value: formatDate(booking.check_out),
                                            icon: CalendarDays,
                                        },
                                        {
                                            label: "Length Of Stay",
                                            value: `${nights} ${nights === 1 ? "Night" : "Nights"}`,
                                            icon: BedDouble,
                                        },
                                    ].map(({ label, value, icon: Icon }) => (
                                        <div
                                            key={label}
                                            className="rounded-2xl border border-[#DCEAE1] bg-[#F8FBF9] p-5"
                                        >
                                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                                                <Icon size={20} />
                                            </div>

                                            <p className="mt-5 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#94A3B8]">
                                                {label}
                                            </p>

                                            <p className="mt-2 text-[15px] font-extrabold text-[#17324D]">
                                                {value}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {/* GUEST INFORMATION */}
                            <section className="rounded-[24px] border border-[#DCEAE1] bg-white p-6 shadow-[0_12px_40px_rgba(6,78,59,0.04)] sm:p-9">
                                <div className="flex items-center gap-4">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E8F7EE] text-[#168B43]">
                                        <UserRound size={23} />
                                    </div>

                                    <div>
                                        <p className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#168B43]">
                                            Booking Information
                                        </p>

                                        <h2 className="mt-1 text-[23px] font-extrabold tracking-tight text-[#17324D]">
                                            Guest Details
                                        </h2>
                                    </div>
                                </div>

                                <div className="mt-7 grid gap-x-10 sm:grid-cols-2">
                                    {guestDetails.map(({ label, value, icon: Icon }) => (
                                        <div
                                            key={label}
                                            className="flex min-w-0 items-start gap-4 border-b border-[#E8F0EA] py-5"
                                        >
                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#F0FAF3] text-[#168B43]">
                                                <Icon size={18} />
                                            </div>

                                            <div className="min-w-0">
                                                <p className="text-[12px] text-[#94A3B8]">
                                                    {label}
                                                </p>

                                                <p className="mt-1 break-words text-[14px] font-extrabold text-[#17324D]">
                                                    {value ?? "—"}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {/* CONFIRMATION */}
                            <section className="flex flex-wrap items-center justify-between gap-5 rounded-[22px] border border-[#B9DEC9] bg-[#EAF8EE] p-6 sm:p-7">
                                <div className="flex items-start gap-4">
                                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-[#168B43]">
                                        <ShieldCheck size={23} />
                                    </div>

                                    <div>
                                        <h3 className="text-[15px] font-extrabold text-[#064E3B]">
                                            Your Booking Is Confirmed
                                        </h3>

                                        <p className="mt-2 max-w-[450px] text-[12px] leading-7 text-[#47715A]">
                                            Keep your receipt for future reference. You can
                                            also access your reservations from My Bookings.
                                        </p>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleMyBookingsClick}
                                    className="inline-flex items-center gap-2 text-[13px] font-extrabold text-[#168B43] hover:text-[#064E3B]"
                                >
                                    My Bookings
                                    <ArrowRight size={17} />
                                </button>
                            </section>
                        </div>

                        {/* PAYMENT SUMMARY */}
                        <aside className="overflow-hidden rounded-[24px] border border-[#DCEAE1] bg-white shadow-[0_15px_50px_rgba(6,78,59,0.06)] lg:sticky lg:top-24">
                            <div className="bg-[#064E3B] p-7 text-white">
                                <div className="flex items-center gap-4">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-[#A5E8B8]">
                                        <ReceiptText size={24} />
                                    </div>

                                    <div>
                                        <p className="text-[10px] font-extrabold uppercase tracking-[0.17em] text-[#B6F2CB]">
                                            Payment Summary
                                        </p>

                                        <h2 className="mt-1 text-[24px] font-extrabold text-white">
                                            Your Receipt
                                        </h2>
                                    </div>
                                </div>

                                <p className="mt-4 text-[12px] leading-7 text-white/70">
                                    A breakdown of your hotel reservation charges.
                                </p>
                            </div>

                            <div className="p-6 sm:p-7">
                                <div className="space-y-5">
                                    {[
                                        ["Room Rate / Night", money(charges.base)],
                                        ["Service Charge (10%)", money(charges.serviceCharge)],
                                        ["VAT (5%)", money(charges.vat)],
                                        ["Tourism Fee / Night", money(charges.tourismFee)],
                                    ].map(([label, value]) => (
                                        <div
                                            key={label}
                                            className="flex items-start justify-between gap-3 text-[12px]"
                                        >
                                            <span className="text-[#718096]">
                                                {label}
                                            </span>

                                            <span className="shrink-0 font-extrabold text-[#17324D]">
                                                {value}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-6 flex items-center justify-between gap-3 border-t border-[#E8F0EA] pt-6">
                                    <span className="text-[13px] font-extrabold text-[#17324D]">
                                        Total Per Night
                                    </span>

                                    <span className="text-[15px] font-extrabold text-[#17324D]">
                                        {money(charges.total)}
                                    </span>
                                </div>

                                {booking.discount_price && (
                                    <div className="mt-5 flex justify-between gap-3 rounded-xl bg-[#F0FAF3] p-4 text-[12px] text-[#168B43]">
                                        <span>Discounted Rate (Recorded)</span>
                                        <span className="font-extrabold">
                                            {money(booking.discount_price)}
                                        </span>
                                    </div>
                                )}

                                <div className="mt-5 flex items-center justify-between text-[12px]">
                                    <span className="text-[#718096]">
                                        Number Of Nights
                                    </span>

                                    <span className="font-extrabold text-[#17324D]">
                                        {nights}
                                    </span>
                                </div>

                                <div className="mt-7 rounded-2xl bg-[#064E3B] p-6 text-white">
                                    <p className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-white/65">
                                        Total Paid
                                    </p>

                                    <p className="mt-3 text-[32px] font-extrabold tracking-tight text-[#A5E8B8]">
                                        {money(totalPaid)}
                                    </p>

                                    <div className="mt-4 flex items-center gap-2 border-t border-white/15 pt-4 text-[11px] text-white/75">
                                        <CheckCircle2 size={16} className="text-[#A5E8B8]" />
                                        Payment Verified
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={downloadInvoice}
                                    className="mt-6 flex w-full items-center justify-center gap-3 rounded-xl bg-[#168B43] px-5 py-4 text-[13px] font-extrabold text-white transition hover:bg-[#087D37]"
                                >
                                    <Download size={18} />
                                    Download Receipt
                                    <ArrowRight size={17} />
                                </button>

                                <button
                                    type="button"
                                    onClick={handleMyBookingsClick}
                                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-[#B9DEC9] bg-[#F8FBF9] px-5 py-4 text-[13px] font-extrabold text-[#17324D] hover:bg-[#E8F7EE]"
                                >
                                    <BookOpen size={17} />
                                    My Bookings
                                </button>

                                <Link
                                    to="/"
                                    className="mt-5 flex items-center justify-center gap-2 text-[12px] font-bold text-[#718096] hover:text-[#168B43]"
                                >
                                    <Home size={16} />
                                    Return To Home
                                </Link>

                                <div className="mt-6 flex items-center justify-center gap-2 border-t border-[#E8F0EA] pt-5 text-center text-[11px] text-[#94A3B8]">
                                    <ShieldCheck size={16} className="text-[#168B43]" />
                                    Secure Payment Confirmation
                                </div>
                            </div>
                        </aside>
                    </div>
                )}
            </div>

            {/* LOGIN POPUP */}
            {showLoginPopup && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#032F28]/75 p-4 backdrop-blur-sm"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="login-dialog-title"
                >
                    <div className="relative w-full max-w-md rounded-[24px] bg-white p-7 text-center shadow-2xl sm:p-9">
                        <button
                            type="button"
                            aria-label="Close"
                            onClick={() => setShowLoginPopup(false)}
                            className="absolute right-5 top-5 text-[#94A3B8] hover:text-[#17324D]"
                        >
                            <X size={21} />
                        </button>

                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#E8F7EE] text-[#168B43]">
                            <LockKeyhole size={29} />
                        </div>

                        <p className="mt-6 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#168B43]">
                            My Account
                        </p>

                        <h3
                            id="login-dialog-title"
                            className="mt-3 text-[29px] font-extrabold tracking-tight text-[#17324D]"
                        >
                            Sign In To Continue
                        </h3>

                        <p className="mt-4 text-[13px] leading-7 text-[#718096]">
                            Log in or create an account to access and manage
                            your hotel reservations.
                        </p>

                        <button
                            type="button"
                            onClick={handleLogin}
                            className="mt-7 w-full rounded-xl bg-[#168B43] px-5 py-4 text-sm font-extrabold text-white hover:bg-[#087D37]"
                        >
                            Login
                        </button>

                        <button
                            type="button"
                            onClick={handleLogin}
                            className="mt-3 w-full rounded-xl border border-[#B9DEC9] px-5 py-4 text-sm font-extrabold text-[#17324D] hover:bg-[#F0FAF3]"
                        >
                            Create Account
                        </button>

                        <button
                            type="button"
                            onClick={() => setShowLoginPopup(false)}
                            className="mt-5 text-xs font-bold text-[#718096]"
                        >
                            Not Now
                        </button>
                    </div>
                </div>
            )}
        </main>
    );
}
